import moment from 'moment';
import React, {useCallback, useEffect, useState} from 'react';
import { useTimeFormatter} from '../../../data/hooks/datetimeHooks';
import { useItemsFilteredByDate } from "../../../data/hooks/datetime/useItemsFilteredByDate";
import {eventMomentFactory} from '../../../data/misc/eventMomentFactory';
import EventTimezone from '../EventTimezone/EventTimezone';
import Grid from '../Grid';
import Tabs from '../Tabs/old';
import UserTimeInfo from '../UserTimeInfo/UserTimeInfo';
import Slot from './Slot';
import styles from './TimeslotSelection.module.scss';
import { useDatesFromItems } from '../../../data/hooks/datetime/useDatesFromItems';

/**
 * TimeslotSelection Component
 * @type TimeslotSelection
 *
 * @prop className                  Class to be added to component
 * @prop date                       Moment instance with selected date (without time; use .startOf('day'))
 * @prop slots                      Array of objects of type timeslot
 * @prop selectedColor              string with color for selected date tab color
 * @prop selectedBackground         string with color for selected date tab background
 * @prop columns                    (optional) Number of columns to show (default: 4)
 * @prop selectedSlot               (optional) Selected timeslot object (uses instance or shallow compare); Adds class 'selected'
 *
 * @prop singleSlotSelectionMode    boolean; when true it will hide the slot  selection grid and show the start and end times of the current selection
 * @prop aboveGridInfo              (optional) Text/Component to show above the slot selection grid
 *
 * @event onSlotSelected(slot)      Triggered when one of the slots is clicked; Sends slot object as argument
 * @event onDateSelected(date)      Triggered when the selected date changes; Sends moment() object as argument
 *
 * @prop locationRequired           boolean; required location_ids array to have at least one value
 *
 *
 * Timeslot required properties: {
 *      start_utc:      string      parseable timestamp
 *      self_blocked:   boolean     adds class isBlocked
 *      self_available: boolean     adds class hasMeeting when false
 * }
 *
 */
const TimeslotSelection = props => {
    const {
            event,
            className,
            date: defaultDate,
            slots,
            selectedColor,
            selectedBackground,
            columns,
            selectedSlot,
            //
            singleSlotSelectionMode = false,
            aboveGridInfo,
            //
            onSlotSelected = slot => {},
            onDateSelected = date => {},
            //
            locationRequired = false
        } = props,
        [date, setDate] = useState(defaultDate),
        dates = useDatesFromItems(event, slots),
        visibleSlots = useItemsFilteredByDate(event, slots, date),
        formatTime = useTimeFormatter();

    // console.log('DEBUG','visibleSlots', visibleSlots);

    const handleDateChange = useCallback(
        date => {
            setDate(date);
            onDateSelected(date);
        },
        [onDateSelected]
    );
    const handleSlotSelected = useCallback(
        slot => {
            onSlotSelected(slot);
        },
        [onSlotSelected]
    );

    // when event changes, adjust date to selected slot date if TZ mode changes
    useEffect(() => {
        if (selectedSlot) {
            setDate(moment(selectedSlot.start_local).startOf('day'));
        } else {
            setDate(null);
        }
    }, [event.show_in_user_timezone]);

    // if date changes, find a slot in the new date and same time (or unset it)
    useEffect(() => {
        if (selectedSlot && date && !date.isSame(selectedSlot.start_local, 'day')) {
            let newSlotStart = eventMomentFactory(event, selectedSlot.start_local)
                    .set('year', date.get('year'))
                    .set('month', date.get('month'))
                    .set('day', date.get('day')),
                newSlot = slots.find(s => eventMomentFactory(event, s.start_local).isSame(newSlotStart, 'minute'));
            console.log('attempting to find a slot at ', newSlotStart.format(), newSlot || 'not found');
            onSlotSelected(newSlot);
        }
    }, [date]);

    useEffect(() => {
        if (!date || !dates.find(d => d.isSame(date, 'day'))) {
            let possibleDates = dates.filter(d => moment().isBefore(d));
            if (!possibleDates.length) {
                possibleDates = dates;
            }
            if (possibleDates.length) {
                handleDateChange(possibleDates[0]);
            }
        }
    }, [date, slots]);

    if (!date) return null;

    return (
        <div className={[styles.TimeslotSelection, className].join(' ')}>
            <Tabs
                minTabWidth={120}
                selectedTabStyle={{
                    color: selectedColor,
                    backgroundColor: selectedBackground,
                }}>
                {dates.map(d => (
                    <button type="button" selected={d.isSame(date, 'day')} onClick={() => handleDateChange(d)} key={d.format('L')}>
                        {d.format('D MMMM')}
                    </button>
                ))}
            </Tabs>
            {singleSlotSelectionMode && selectedSlot ? (
                <>
                    <div className={styles.currentSelection} onClick={() => handleSlotSelected(null)}>
                        {formatTime(selectedSlot.start_local)} - {formatTime(selectedSlot.end_local)}
                    </div>
                    <UserTimeInfo event={event} time={selectedSlot.start_local} />
                </>
            ) : (
                <>
                    {aboveGridInfo ? <div className={styles.aboveGridInfo}>{aboveGridInfo}</div> : null}
                    <EventTimezone className={styles.timezone} event={event} />
                    <Grid columns={columns || 4}>
                        {visibleSlots.map(slot => (
                            <Slot
                                selected={slot === selectedSlot}
                                slot={slot}
                                key={[slot.start, slot.end].join()}
                                onClick={() => handleSlotSelected(slot)}
                                formatTime={formatTime}
                                locationRequired={locationRequired}
                            />
                        ))}
                    </Grid>
                </>
            )}
        </div>
    );
};

export default TimeslotSelection;
